import React from 'react';
import { Helmet } from 'react-helmet-async';

const DEFAULT_SITE_URL = 'https://omniscribe.ai';
const DEFAULT_TITLE = 'AI Audio & Video Transcription Online | OmniScribe';
const DEFAULT_DESCRIPTION =
  'Convert audio and video to accurate text in 98+ languages. Add speaker labels, summaries, mind maps, and TXT, DOCX, PDF, SRT, VTT, CSV exports.';
const DEFAULT_KEYWORDS = [
  'audio to text',
  'video to text',
  'AI transcription',
  'speech to text',
  'MP3 to text',
  'MP4 to text',
  'YouTube transcription',
  'transcript summary',
  'subtitle generator',
];

export const SITE_URL = ((import.meta.env.VITE_SITE_URL as string | undefined)?.trim() || DEFAULT_SITE_URL).replace(/\/+$/, '');
export const SITE_NAME = 'OmniScribe';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/omniscribe_logo.png`;
const INDEX_ROBOTS_DIRECTIVES = 'index, follow, max-snippet:160, max-image-preview:large';

type JsonLd = Record<string, unknown>;

interface SEOProps {
  title?: string;
  description?: string;
  path?: string;
  image?: string;
  type?: 'website' | 'article';
  keywords?: string[];
  noindex?: boolean;
  jsonLd?: JsonLd | JsonLd[];
}

const normalizePath = (path = '/') => {
  if (/^https?:\/\//.test(path)) {
    return path;
  }
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
};

export const canonicalUrl = (path = '/') => normalizePath(path);

export const SEO: React.FC<SEOProps> = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  path = '/',
  image = DEFAULT_OG_IMAGE,
  type = 'website',
  keywords = DEFAULT_KEYWORDS,
  noindex = false,
  jsonLd,
}) => {
  const canonical = canonicalUrl(path);
  const robots = noindex ? 'noindex, nofollow' : INDEX_ROBOTS_DIRECTIVES;
  const jsonLdItems = !jsonLd ? [] : Array.isArray(jsonLd) ? jsonLd : [jsonLd];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(', ')} />
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={robots} />

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {jsonLdItems.map((item, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
};

export const organizationJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'NanoUltra, LLC',
  legalName: 'NanoUltra, LLC',
  url: 'https://nanoultra.co',
  foundingDate: '2026-01-13',
  description: 'Delaware limited liability company, File Number 10470896',
  identifier: {
    '@type': 'PropertyValue',
    name: 'Delaware Division of Corporations File Number',
    value: '10470896',
  },
  logo: DEFAULT_OG_IMAGE,
  email: 'support@omniscribe.ai',
  brand: {
    '@type': 'Brand',
    name: SITE_NAME,
    url: SITE_URL,
  },
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'support@omniscribe.ai',
    contactType: 'customer support',
    availableLanguage: ['English', 'Chinese'],
  },
  knowsAbout: [
    'audio transcription',
    'video transcription',
    'speech recognition',
    'speaker identification',
    'AI summarization',
    'subtitle generation',
  ],
  sameAs: [SITE_URL],
};

export const websiteJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  inLanguage: 'en',
  publisher: {
    '@type': 'Organization',
    name: 'NanoUltra, LLC',
    legalName: 'NanoUltra, LLC',
    url: 'https://nanoultra.co',
    foundingDate: '2026-01-13',
    description: 'Delaware limited liability company, File Number 10470896',
  },
};

export const breadcrumbJsonLd = (items: Array<{ name: string; path: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: canonicalUrl(item.path),
  })),
});
